import { useState } from "react";

import {
  Filter,
  X,
} from "lucide-react";

import FilterDropdown from "./FilterDropdown";
import SearchBar from "./SearchBar";

/**
 * Modern Mobile Filter Panel
 *
 * - Glassmorphism
 * - Bottom sheet animation
 * - Mobile friendly
 * - Fix pointer-events issue
 * - Professional modern UI
 */

export default function MobileFilterPanel({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Cari...",
  filters = [],
  onApply,
  onReset,
  isOpen: controlledIsOpen,
  onToggle,
}) {
  const [internalIsOpen, setInternalIsOpen] =
    useState(false);

  const isControlled =
    controlledIsOpen !== undefined &&
    onToggle !== undefined;

  const isOpen = isControlled
    ? controlledIsOpen
    : internalIsOpen;

  const setIsOpen = isControlled
    ? onToggle
    : setInternalIsOpen;

  return (
    <>
      {/* SEARCH + FILTER BUTTON */}
      {!isControlled && (
        <div className="relative z-30 flex gap-2 mb-4 md:hidden">
          {/* Search */}
          <div className="flex-1">
            <SearchBar
              value={searchValue}
              onChange={onSearchChange}
              placeholder={searchPlaceholder}
            />
          </div>

          {/* Filter Button */}
          <button
            onClick={() => setIsOpen(true)}
            className="
              group
              relative
              overflow-hidden

              h-11
              px-4

              rounded-2xl
              border border-cyan-500/10

              bg-cyan-600
              text-white

              text-sm
              font-medium

              shadow-lg
              shadow-cyan-200/50

              transition-all
              duration-300

              hover:bg-cyan-700
              hover:-translate-y-0.5
              hover:shadow-xl
            "
          >
            {/* Shine */}
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/0 via-white/20 to-cyan-400/0 opacity-0 group-hover:opacity-100 group-hover:animate-[shine_1.5s_ease-in-out] pointer-events-none" />

            <div className="relative z-10 flex items-center gap-2">
              <Filter
                size={16}
                className="group-hover:rotate-12 transition-transform"
              />

              <span>Filter</span>
            </div>
          </button>
        </div>
      )}

      {/* OVERLAY */}
      {isOpen && (
        <div className="fixed inset-0 z-[999] md:hidden">
          {/* BACKDROP */}
          <div
            className="
              absolute
              inset-0

              bg-black/40
              backdrop-blur-sm

              pointer-events-auto
            "
            onClick={() => setIsOpen(false)}
          />

          {/* BOTTOM SHEET */}
          <div
            className="
              absolute
              bottom-0
              left-0
              right-0

              rounded-t-[32px]

              border-t
              border-gray-200

              bg-white/90
              backdrop-blur-2xl

              shadow-2xl

              max-h-[85vh]
              overflow-y-auto

              animate-[slideUp_.25s_ease-out]
            "
          >
            {/* Glow */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-[-80px] left-[-40px] w-[220px] h-[220px] bg-cyan-200/30 rounded-full blur-3xl" />

              <div className="absolute bottom-[-100px] right-[-60px] w-[260px] h-[260px] bg-blue-200/20 rounded-full blur-3xl" />
            </div>

            {/* CONTENT */}
            <div className="relative z-10 p-5">
              {/* HEADER */}
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-100 bg-cyan-50 mb-3">
                    <div className="w-2 h-2 rounded-full bg-cyan-500 animate-pulse" />

                    <span className="text-[11px] uppercase tracking-widest text-cyan-700">
                      Mobile Filter
                    </span>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-900">
                    Filter & Urutkan
                  </h3>

                  <p className="text-xs text-gray-500 mt-1">
                    Sesuaikan data yang ingin ditampilkan
                  </p>
                </div>

                {/* Close */}
                <button
                  onClick={() =>
                    setIsOpen(false)
                  }
                  className="
                    group

                    w-10
                    h-10

                    rounded-2xl

                    border
                    border-gray-200

                    bg-white

                    flex
                    items-center
                    justify-center

                    shadow-sm

                    hover:bg-gray-50
                    transition-all
                  "
                >
                  <X
                    size={18}
                    className="text-gray-600 group-hover:rotate-90 transition-transform duration-300"
                  />
                </button>
              </div>

              {/* FILTERS */}
              <div className="space-y-4">
                {filters.map(
                  (filter, idx) =>
                    filter.show !== false ? (
                      <div
                        key={idx}
                        className="relative"
                      >
                        <FilterDropdown
                          label={filter.label}
                          options={
                            filter.options
                          }
                          value={filter.value}
                          onChange={
                            filter.onChange
                          }
                          placeholder={
                            filter.placeholder
                          }
                        />
                      </div>
                    ) : null,
                )}
              </div>

              {/* ACTIONS */}
              <div className="grid grid-cols-2 gap-3 mt-6">
                {/* RESET */}
                <button
                  onClick={() => {
                    onReset?.();
                  }}
                  className="
                    h-12

                    rounded-2xl

                    border
                    border-gray-200

                    bg-white

                    text-sm
                    font-medium
                    text-gray-700

                    shadow-sm

                    hover:bg-gray-50
                    hover:shadow-md

                    transition-all
                    duration-300
                  "
                >
                  Reset
                </button>

                {/* APPLY */}
                <button
                  onClick={() => {
                    onApply?.();
                    setIsOpen(false);
                  }}
                  className="
                    group
                    relative
                    overflow-hidden

                    h-12

                    rounded-2xl

                    bg-cyan-600
                    text-white

                    text-sm
                    font-medium

                    shadow-lg
                    shadow-cyan-200/50

                    hover:bg-cyan-700
                    hover:shadow-xl

                    transition-all
                    duration-300
                  "
                >
                  {/* Shine */}
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/0 via-white/20 to-cyan-400/0 opacity-0 group-hover:opacity-100 group-hover:animate-[shine_1.5s_ease-in-out] pointer-events-none" />

                  <span className="relative z-10">
                    Terapkan
                  </span>
                </button>
              </div>

              {/* Safe Area */}
              <div className="h-4" />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
